import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';

export type Props = {
  dataTestId?: string;
  lowQualitySrc: string;
  highQualitySrc: string;
  srcset?: string;
  width: number;
};

const ProgressiveImage: React.FC<Props> = ({
  srcset,
  lowQualitySrc,
  highQualitySrc,
  width,
}) => {
  const [source, setSource] = useState<string>(lowQualitySrc);
  const [loaded, setLoaded] = useState<boolean>(false);
  const localHighQualitySrc = highQualitySrc ?? lowQualitySrc;
  const localLowQualitySrc = lowQualitySrc ?? highQualitySrc;

  useEffect(() => {
    let sourceWasUpdated = false;
    setLoaded(false);
    setSource(localLowQualitySrc);
    const img = new Image();
    if (srcset) {
      img.srcset = srcset;
    } else {
      img.src = localHighQualitySrc;
    }
    img.onload = () => {
      if (!sourceWasUpdated) {
        setSource(localHighQualitySrc);
        setLoaded(true);
      }
    };
    return () => {
      sourceWasUpdated = true;
    };
  }, [localLowQualitySrc, localHighQualitySrc, srcset]);

  return (
    <LocalImage
      src={source}
      srcSet={loaded ? srcset : undefined}
      $blurred={!loaded}
      $width={width}
    />
  );
};

const LocalImage = styled.img<{ $blurred: boolean; $width: number }>(
  ({ $blurred, $width }) => css`
    ${$blurred ? 'filter: blur(20px); transition: filter 1s ease-out;' : ''}
    max-width: ${$width}px;
    max-height: ${$width}px;
    width: 100%;

    display: block;
    page-break-inside: avoid;
  `,
);

export default ProgressiveImage;
